import { Component } from '@theme/component';

const MAX_FILES = 5;
const MAX_FILE_BYTES = 50 * 1000 * 1000;
const MAX_TOTAL_BYTES = 50 * 1000 * 1000;
const FILE_EXTENSION = /\.(jpe?g|png|gif|webp|mp4|mov|pdf|docx?|pptx?|txt|xlsx?|csv|rar|zip|7z)$/i;

/**
 * @typedef {object} SupportFormRefs
 * @property {HTMLFormElement} form
 * @property {HTMLFieldSetElement} fields
 * @property {HTMLFieldSetElement} submitControls
 * @property {HTMLInputElement} fileInput
 * @property {HTMLUListElement} fileList
 * @property {HTMLDivElement} status
 * @property {HTMLDivElement} [successPanel]
 * @property {HTMLHeadingElement} [successHeading]
 * @property {HTMLParagraphElement} [successTicket]
 * @property {HTMLButtonElement} [submitButton]
 * @property {HTMLParagraphElement} [intro]
 * @property {HTMLParagraphElement} [requiredHint]
 */

/** @extends {Component<SupportFormRefs>} */
class ZendeskContactForm extends Component {
  requiredRefs = ['form', 'fields', 'submitControls', 'fileInput', 'fileList', 'status'];
  /** @type {File[]} */
  #files = [];
  /** @type {AbortController | undefined} */
  #request;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timeout;
  /** @type {URL | undefined} */
  #endpoint;
  #submissionId = '';
  /** @type {number | undefined} */
  #confirmedTicket;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cancelRequest();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancelRequest();
  }

  #initialize() {
    this.#endpoint = undefined;
    const configuredEndpoint = this.dataset.endpoint?.trim();
    if (configuredEndpoint) {
      try {
        const endpoint = new URL(configuredEndpoint, window.location.origin);
        const localPreview = ['127.0.0.1', 'localhost'].includes(window.location.hostname);
        if (
          !endpoint.username && !endpoint.password &&
          (endpoint.protocol === 'https:' ||
            (localPreview && endpoint.protocol === 'http:' && ['127.0.0.1', 'localhost'].includes(endpoint.hostname)))
        ) {
          this.#endpoint = endpoint;
        }
      } catch {
        // 无效地址保持不可提交，避免误发到 Shopify 联系接口。
      }
    }
    this.refs.form.removeAttribute('aria-busy');
    this.#renderFiles();
    if (this.#confirmedTicket === undefined) this.refs.status.textContent = this.#endpoint ? '' : this.dataset.unavailable ?? '';
    this.#renderState();
  }

  /** 同一实例重渲染时保留确认结果，只有主动重新填写才能解除成功状态。 */
  #renderState() {
    const confirmed = this.#confirmedTicket !== undefined;
    const sending = Boolean(this.#request);
    const { successPanel, successTicket, submitButton } = this.refs;
    this.refs.form.dataset.state = confirmed ? 'success' : sending ? 'sending' : 'ready';
    this.refs.fields.disabled = confirmed || sending;
    this.refs.submitControls.disabled = confirmed || sending || !this.#endpoint;
    if (successPanel) {
      successPanel.hidden = !confirmed;
      this.refs.fields.hidden = confirmed;
      this.refs.submitControls.hidden = confirmed;
      if (this.refs.intro) this.refs.intro.hidden = confirmed;
      if (this.refs.requiredHint) this.refs.requiredHint.hidden = confirmed;
    }
    if (submitButton) {
      submitButton.textContent = (sending ? submitButton.dataset.sendingLabel : submitButton.dataset.idleLabel) ?? '';
    }
    if (successTicket) {
      successTicket.textContent = confirmed ? (this.dataset.ticketNumber ?? '').replace('[number]', String(this.#confirmedTicket)) : '';
    }
    if (confirmed) {
      const message = `${this.refs.successHeading?.textContent ?? this.dataset.success} ${(this.dataset.ticketNumber ?? '').replace('[number]', String(this.#confirmedTicket))}`;
      if (this.refs.status.textContent !== message) this.refs.status.textContent = message;
    }
  }

  /** 用户主动开始另一项需求，不自动发送新请求。 */
  restartInquiry() {
    if (this.#confirmedTicket === undefined || this.#request) return;
    this.#confirmedTicket = undefined;
    this.refs.form.reset();
    for (const field of this.refs.form.querySelectorAll('input:not([type="file"]), textarea')) {
      if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) field.value = '';
    }
    this.#files = [];
    this.invalidateSubmission();
    this.#renderFiles();
    this.refs.status.textContent = this.#endpoint ? '' : this.dataset.unavailable ?? '';
    this.#renderState();
    const name = this.refs.form.elements.namedItem('name');
    if (name instanceof HTMLInputElement) name.focus();
  }

  /** 字段或附件改变后，下一次提交使用新的幂等标识。 */
  invalidateSubmission() {
    this.#submissionId = '';
  }

  /** @param {Event} event */
  addFiles(event) {
    if (!(event.target instanceof HTMLInputElement) || this.#request || this.#confirmedTicket !== undefined) return;
    const candidates = [...this.#files];
    for (const file of event.target.files ?? []) {
      if (!candidates.some((item) => item.name === file.name && item.size === file.size && item.lastModified === file.lastModified)) {
        candidates.push(file);
      }
    }
    event.target.value = '';
    if (candidates.length > MAX_FILES) return this.#announce('tooMany');
    if (candidates.some((file) => !file.size || !FILE_EXTENSION.test(file.name))) return this.#announce('invalidFile');
    if (candidates.some((file) => file.size > MAX_FILE_BYTES) || candidates.reduce((sum, file) => sum + file.size, 0) > MAX_TOTAL_BYTES) {
      return this.#announce('tooLarge');
    }
    this.#files = candidates;
    this.invalidateSubmission();
    this.#renderFiles();
    this.refs.status.textContent = this.#endpoint ? '' : this.dataset.unavailable ?? '';
  }

  /** @param {MouseEvent} event */
  removeFile(event) {
    if (!(event.target instanceof HTMLButtonElement) || this.#request || this.#confirmedTicket !== undefined) return;
    const index = Number(event.target.dataset.index);
    if (!Number.isInteger(index) || index < 0 || index >= this.#files.length) return;
    this.#files.splice(index, 1);
    this.invalidateSubmission();
    this.#renderFiles();
    this.refs.status.textContent = this.#endpoint ? '' : this.dataset.unavailable ?? '';
    const nextButton = this.refs.fileList.querySelectorAll('button')[index];
    (nextButton ?? this.refs.fileInput).focus();
  }

  #renderFiles() {
    const items = this.#files.map((file, index) => {
      const item = document.createElement('li');
      item.className = 'zendesk-contact__file';
      const name = document.createElement('span');
      name.className = 'zendesk-contact__file-name';
      name.textContent = `${file.name} (${(file.size / 1000 / 1000).toFixed(2)} MB)`;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'button-unstyled zendesk-contact__remove';
      remove.dataset.index = String(index);
      remove.setAttribute('on:click', '/removeFile');
      remove.setAttribute('aria-label', (this.dataset.removeFile ?? '').replace('[filename]', file.name));
      remove.textContent = '×';
      item.append(name, remove);
      return item;
    });
    this.refs.fileList.replaceChildren(...items);
    this.refs.fileList.hidden = !items.length;
  }

  /** @param {SubmitEvent} event */
  async submitRequest(event) {
    event.preventDefault();
    if (this.#request || this.#confirmedTicket !== undefined) return;
    if (!this.#endpoint) return this.#announce('unavailable');
    if (!this.refs.form.reportValidity()) return;

    const fields = new FormData(this.refs.form);
    const body = this.dataset.mode === 'solutions' ? this.#solutionsBody(fields) : fields;
    if (!body) return;
    for (const file of this.#files) body.append('attachments', file, file.name);
    this.#submissionId ||= crypto.randomUUID();
    body.set('submission_id', this.#submissionId);

    const controller = new AbortController();
    this.#request = controller;
    this.#timeout = setTimeout(() => controller.abort(), 90000);
    this.refs.fields.disabled = true;
    this.refs.submitControls.disabled = true;
    this.refs.form.setAttribute('aria-busy', 'true');
    this.#announce('sending');
    this.#renderState();

    try {
      const response = await fetch(this.#endpoint, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'omit',
        body,
        signal: controller.signal,
      });
      if (this.#request !== controller) return;
      if (!response.ok) {
        const error = await response.json().catch(() => null);
        if (this.#request !== controller) return;
        this.#announce(response.status === 429 ? 'rateLimit' : response.status === 413 ? 'tooLarge' : error?.error === 'invalid_file' ? 'invalidFile' : 'error');
        return;
      }
      const result = await response.json();
      if (this.#request !== controller) return;
      if (!Number.isSafeInteger(result.ticket_id) || result.ticket_id <= 0) throw new Error('Missing ticket confirmation');

      this.refs.form.reset();
      this.#files = [];
      this.invalidateSubmission();
      this.#renderFiles();
      this.#confirmedTicket = result.ticket_id;
      this.#renderState();
      const confirmation = this.refs.successHeading ?? this.refs.status;
      confirmation.focus({ preventScroll: true });
      confirmation.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    } catch {
      if (this.#request === controller && this.isConnected) this.#announce('error');
    } finally {
      if (this.#request === controller) {
        this.#cancelRequest();
        this.refs.form.removeAttribute('aria-busy');
        this.#renderState();
      }
    }
  }

  /**
   * 专题字段仅映射到服务端允许的字段；按 Python 字符数校验，保留完整内容。
   * @param {FormData} fields
   * @returns {FormData | undefined}
   */
  #solutionsBody(fields) {
    const name = String(fields.get('name') ?? '').trim();
    const email = String(fields.get('email') ?? '').trim();
    const application = String(fields.get('application') ?? '').trim();
    const quantity = String(fields.get('expected_quantity') ?? '');
    const requirements = String(fields.get('project_requirements') ?? '').trim();
    if (
      !name || !application || !requirements || !/^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(email) ||
      (quantity && !/^[0-9]+$/.test(quantity)) ||
      [name, email, application, quantity, requirements].some((value) => value.includes('\0'))
    ) {
      this.#announce('invalidFields');
      return;
    }

    const subject = `[Solutions] ${application}`;
    const paragraphs = [`Application or Use Case: ${application}`];
    if (quantity) paragraphs.push(`Expected Quantity: ${quantity}`);
    paragraphs.push(`Project Requirements:\n${requirements}`);
    // 排除锚点和预览参数，确保页内导航不会改变重试请求的幂等内容。
    paragraphs.push(`Page: ${window.location.origin}${window.location.pathname}`);
    const message = paragraphs.join('\n\n');
    if ([...name].length > 100 || [...email].length > 254 || [...subject].length > 200 || [...message].length > 10000) {
      this.#announce('tooLong');
      return;
    }

    const body = new FormData();
    for (const [key, value] of Object.entries({ name, email, subject, message })) body.set(key, value);
    return body;
  }

  #cancelRequest() {
    this.#request?.abort();
    this.#request = undefined;
    clearTimeout(this.#timeout);
    this.#timeout = undefined;
  }

  /** @param {string} key */
  #announce(key) {
    this.refs.status.textContent = this.dataset[key] ?? this.dataset.error ?? '';
  }
}

if (!customElements.get('zendesk-contact-form')) {
  customElements.define('zendesk-contact-form', ZendeskContactForm);
}
